import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  PARTNER_CHART_PALETTE,
  buildChartTheme,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const partnerSplitLine = {
  lineStyle: { type: 'dashed', color: 'rgba(148, 163, 184, 0.32)', width: 1 },
};

const PartnerSingleLineChart = ({
  data,
  yAxisLabel = 'Narrative Runs',
  color = '#60A5FA',
  renderZeroWhenEmpty = false,
  zeroXAxis = [],
  zeroSeriesNames = [],
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasSeriesData = Array.isArray(data?.series) && data.series.length > 0;
  const hasSingleData = Array.isArray(data?.data) && data.data.length > 0;
  const hasXAxis = Array.isArray(data?.xData) && data.xData.length > 0;

  const fallbackXAxis = zeroXAxis.length ? zeroXAxis : ['No activity'];
  const shouldRenderZeroChart = renderZeroWhenEmpty && (!hasXAxis || (!hasSingleData && !hasSeriesData));

  if (!shouldRenderZeroChart && (!hasXAxis || (!hasSingleData && !hasSeriesData))) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = shouldRenderZeroChart
    ? {
        xData: fallbackXAxis,
        series: zeroSeriesNames.length
          ? zeroSeriesNames.map((name, index) => ({
              name,
              data: new Array(fallbackXAxis.length).fill(0),
              color: PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
            }))
          : null,
        data: zeroSeriesNames.length ? null : new Array(fallbackXAxis.length).fill(0),
      }
    : data;

  const series =
    normalizedData?.series?.length
      ? normalizedData.series.map((item, index) => ({
          name: item.name,
          type: 'line',
          data: item.data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: item.color || PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
          },
          itemStyle: { color: item.color || PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length] },
        }))
      : [{
          name: yAxisLabel,
          type: 'line',
          data: normalizedData.data || [],
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color },
          itemStyle: { color },
        }];
  const isAllZero = series.every((item) =>
    (item?.data || []).every((value) => Number(value) === 0)
  );

  const options = {
    animation: false,
    grid: theme.gridStandard,
    tooltip: { ...theme.tooltipBase, trigger: 'axis' },
    legend: normalizedData?.series?.length
      ? {
          ...theme.legendBase,
          type: 'scroll',
          bottom: 0,
          left: 'center',
          icon: 'circle',
          formatter: (name) => truncateChartLabel(name, 18),
        }
      : undefined,
    xAxis: {
      type: 'category',
      data: normalizedData.xData || [],
      boundaryGap: false,
      axisLabel: { ...theme.axisLabel, fontWeight: 500 },
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameTextStyle: theme.axisNameStyle,
      axisLabel: theme.axisLabel,
      min: 0,
      max: isAllZero ? 1 : undefined,
      splitLine: partnerSplitLine,
    },
    series,
  };

  return (
    <ReactECharts
      option={options}
      style={{ height: `${isLaptopCompact ? 282 : 300}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default PartnerSingleLineChart;
